import { useState } from "react";
import { Plus, Edit, Trash2, Star, MoveUp, MoveDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  useAllServicePricingPackages,
  useCreateServicePricingPackage,
  useUpdateServicePricingPackage,
  useDeleteServicePricingPackage,
  ServicePricingPackage,
} from "@/hooks/useServicePricingPackages";
import { useServices } from "@/hooks/useServices";

export const ServicePricingPackagesSection = () => {
  const { data: packages, isLoading } = useAllServicePricingPackages();
  const { data: services } = useServices();
  const createPackage = useCreateServicePricingPackage();
  const updatePackage = useUpdateServicePricingPackage();
  const deletePackage = useDeleteServicePricingPackage();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<ServicePricingPackage | null>(null);
  const [formData, setFormData] = useState({
    service_id: "",
    package_name: "",
    package_name_en: "",
    package_name_ru: "",
    price: 0,
    currency: "AMD",
    description: "",
    description_en: "",
    description_ru: "",
    features: [] as string[],
    is_popular: false,
    is_active: true,
    order_index: 0,
  });
  const [newFeature, setNewFeature] = useState("");

  const handleOpenDialog = (pkg?: ServicePricingPackage) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        service_id: pkg.service_id,
        package_name: pkg.package_name,
        package_name_en: pkg.package_name_en || "",
        package_name_ru: pkg.package_name_ru || "",
        price: pkg.price,
        currency: pkg.currency,
        description: pkg.description || "",
        description_en: pkg.description_en || "",
        description_ru: pkg.description_ru || "",
        features: pkg.features || [],
        is_popular: pkg.is_popular,
        is_active: pkg.is_active,
        order_index: pkg.order_index,
      });
    } else {
      setEditingPackage(null);
      setFormData({
        service_id: "",
        package_name: "",
        package_name_en: "",
        package_name_ru: "",
        price: 0,
        currency: "AMD",
        description: "",
        description_en: "",
        description_ru: "",
        features: [],
        is_popular: false,
        is_active: true,
        order_index: 0,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingPackage) {
      await updatePackage.mutateAsync({
        id: editingPackage.id,
        ...formData,
      });
    } else {
      await createPackage.mutateAsync(formData);
    }
    
    setDialogOpen(false);
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()],
      });
      setNewFeature("");
    }
  };

  const handleRemoveFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  const handleDelete = async (id: string) => {
    if (confirm("Վստա՞հ եք, որ ցանկանում եք ջնջել այս գնային փաթեթը:")) {
      await deletePackage.mutateAsync(id);
    }
  };

  if (isLoading) {
    return <div>Բեռնվում է...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold">Գնային փաթեթներ</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Ավելացնել փաթեթ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPackage ? "Խմբագրել փաթեթը" : "Նոր գնային փաթեթ"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="service_id">Ծառայություն *</Label>
                <Select
                  value={formData.service_id}
                  onValueChange={(value) => setFormData({ ...formData, service_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ընտրել ծառայություն" />
                  </SelectTrigger>
                  <SelectContent>
                    {services?.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="package_name">Անուն (հայերեն) *</Label>
                  <Input
                    id="package_name"
                    value={formData.package_name}
                    onChange={(e) => setFormData({ ...formData, package_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="package_name_en">Անուն (անգլերեն)</Label>
                  <Input
                    id="package_name_en"
                    value={formData.package_name_en}
                    onChange={(e) => setFormData({ ...formData, package_name_en: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="package_name_ru">Անուն (ռուսերեն)</Label>
                  <Input
                    id="package_name_ru"
                    value={formData.package_name_ru}
                    onChange={(e) => setFormData({ ...formData, package_name_ru: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">Գին *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="currency">Արժույթ</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AMD">AMD</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="order_index">Հերթականություն</Label>
                  <Input
                    id="order_index"
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({ ...formData, order_index: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Նկարագրություն (հայերեն)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="description_en">Նկարագրություն (անգլերեն)</Label>
                <Textarea
                  id="description_en"
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="description_ru">Նկարագրություն (ռուսերեն)</Label>
                <Textarea
                  id="description_ru"
                  value={formData.description_ru}
                  onChange={(e) => setFormData({ ...formData, description_ru: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label>Հատկություններ</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Նոր հատկություն"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddFeature();
                      }
                    }}
                  />
                  <Button type="button" onClick={handleAddFeature}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                      <span>{feature}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFeature(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_popular"
                    checked={formData.is_popular}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_popular: checked })}
                  />
                  <Label htmlFor="is_popular">Հանրաճանաչ</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Ակտիվ</Label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Չեղարկել
                </Button>
                <Button type="submit">
                  {editingPackage ? "Պահպանել" : "Ավելացնել"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {packages?.map((pkg: any) => (
          <Card key={pkg.id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold">{pkg.package_name}</h3>
                  {pkg.is_popular && (
                    <Badge variant="default" className="gap-1">
                      <Star className="h-3 w-3" />
                      Հանրաճանաչ
                    </Badge>
                  )}
                  {!pkg.is_active && (
                    <Badge variant="secondary">Անակտիվ</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Ծառայություն: {pkg.services?.title}
                </p>
                <p className="text-2xl font-bold mb-2">
                  {pkg.price.toLocaleString()} {pkg.currency}
                </p>
                {pkg.description && (
                  <p className="text-sm text-muted-foreground mb-2">{pkg.description}</p>
                )}
                {pkg.features && pkg.features.length > 0 && (
                  <ul className="text-sm space-y-1">
                    {pkg.features.map((feature: string, idx: number) => (
                      <li key={idx}>✓ {feature}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenDialog(pkg)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(pkg.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {packages?.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          Գնային փաթեթներ դեռ ավելացված չեն
        </Card>
      )}
    </div>
  );
};
